import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Bell, Boxes, LogOut, Menu, Package, User, X } from 'lucide-react';
import { useAuthStore } from '@/store/auth.store';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/', label: '둘러보기', end: true },
  { to: '/bundles', label: '번들', end: false },
  { to: '/#how', label: '이용방법', end: false },
];

export function Header() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  const logout = useAuthStore((s) => s.logout);

  const [menuOpen, setMenuOpen] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/');
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/70 backdrop-blur-xl">
      <div className="container flex h-16 items-center justify-between gap-4">
        <div className="flex items-center gap-3 md:gap-8">
          <button
            type="button"
            aria-label="메뉴"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-accent md:hidden"
            onClick={() => setMenuOpen((v) => !v)}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-500 text-white shadow-sm">
              <Boxes className="h-5 w-5" />
            </span>
            <span className="text-lg font-extrabold tracking-tight">
              MOA<span className="text-primary">.</span>
            </span>
          </Link>
          <nav className="hidden gap-1 md:flex">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'rounded-full px-3.5 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
                    isActive && 'bg-accent text-accent-foreground',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          {isAuthenticated ? (
            <>
              <Link
                to="/notifications"
                aria-label="알림"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-accent"
              >
                <Bell className="h-5 w-5" />
              </Link>
              <Link
                to="/me/orders"
                aria-label="주문 내역"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-accent"
              >
                <Package className="h-5 w-5" />
              </Link>
              <Link
                to="/me"
                className="flex items-center gap-2 rounded-full py-1 pl-1 pr-3 text-sm hover:bg-accent"
              >
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                ) : (
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary/20 to-emerald-500/20 text-primary">
                    <User className="h-4 w-4" />
                  </span>
                )}
                <span className="hidden sm:inline">{user?.displayName}</span>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} aria-label="로그아웃">
                <LogOut className="h-5 w-5" />
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => navigate('/auth/login')}>
                로그인
              </Button>
              <Button
                className="rounded-full bg-gradient-to-r from-primary to-emerald-500 shadow-sm hover:opacity-95"
                onClick={() => navigate('/auth/register')}
              >
                무료로 시작
              </Button>
            </>
          )}
        </div>
      </div>

      {/* 모바일 네비 패널 */}
      {menuOpen && (
        <nav className="border-t bg-background px-4 py-2 md:hidden">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMenuOpen(false)}
              className={({ isActive }) =>
                cn(
                  'block rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  isActive && 'bg-accent text-accent-foreground',
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
